import { NavLink } from "react-router-dom"
import { useConfig, useFeatures } from "@/config/context"
import type { FeatureConfig, IconName } from "@/config/types"
import { useTranslation } from "@/i18n"
import { useNostr } from "@/state/NostrContext"
import { useAuth } from "@/state/AuthContext"
import { Brand } from "./Brand"
import { Icon } from "./Icon"
import { cn } from "./helpers"

const DEFAULT_LABELS: Record<string, string> = {
  home: "Home",
  explore: "Explore",
  notifications: "Notifications",
  bookmarks: "Bookmarks",
  search: "Search",
  profile: "Profile",
  settings: "Settings",
}

function featureEnabled(id: string, features: FeatureConfig): boolean {
  if (id === "explore") return features.explore
  if (id === "notifications") return features.notifications
  if (id === "bookmarks") return features.bookmarks
  if (id === "search") return features.search
  return true
}

/** Navigation entries from the configuration, filtered by feature flags. */
export function useNavigationItems() {
  const config = useConfig()
  const features = useFeatures()
  const { t } = useTranslation()
  return config.navigation.items
    .filter((item) => item.visible && featureEnabled(item.id, features))
    .map((item) => ({
      ...item,
      label: t(item.label ?? DEFAULT_LABELS[item.id] ?? item.id),
    }))
}

export function RelayConnectionBadge() {
  const { statuses } = useNostr()
  const { t } = useTranslation()
  const connected = statuses.filter((status) => status.state === "connected").length
  const total = statuses.length
  const tone = total === 0 ? "muted" : connected === 0 ? "bad" : connected < total ? "warn" : "ok"

  return (
    <span className={`connection-badge connection-${tone}`} title={t("relays connected", { connected, total })}>
      <span className="connection-dot" aria-hidden="true" />
      {connected}/{total}
    </span>
  )
}

export interface SidebarProps {
  onCompose: () => void
  onAccount: () => void
}

export function Sidebar({ onCompose, onAccount }: SidebarProps) {
  const { t } = useTranslation()
  const { canSign, pubkey, method } = useAuth()
  const items = useNavigationItems()

  return (
    <aside className="sidebar">
      <div className="sidebar-top">
        <Brand />
        <RelayConnectionBadge />
      </div>

      <nav className="sidebar-nav" aria-label="Main">
        {items.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            end={item.path === "/"}
            className={({ isActive }) => cn("nav-link", isActive && "nav-link-active")}
          >
            <Icon name={item.icon as IconName} size={20} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-actions">
        <button type="button" className="btn btn-primary btn-block" onClick={onCompose}>
          <Icon name="pen" size={16} />
          {t("Publish a note")}
        </button>
        <button type="button" className="sidebar-account" onClick={onAccount}>
          <Icon name={canSign ? "user" : "logout"} size={18} />
          <span className="sidebar-account-text">
            <span className="sidebar-account-title">
              {pubkey ? (method === "npub" ? t("Read-only session") : `${pubkey.slice(0, 10)}…`) : t("Log in")}
            </span>
            <span className="sidebar-account-sub">{pubkey ? t("Account") : t("NIP-07, nsec or npub")}</span>
          </span>
        </button>
      </div>
    </aside>
  )
}

/** Bottom navigation for small screens. */
export function MobileNavigation({ onAccount }: { onAccount: () => void }) {
  const items = useNavigationItems()
  const mobileItems = items.filter((item) =>
    ["home", "explore", "search", "notifications", "profile"].includes(item.id),
  )
  // Shorter labels so six slots fit on a 360 px screen without truncation.
  const shortLabels: Record<string, string> = { notifications: "Alerts" }

  return (
    <nav className="mobile-nav" aria-label="Main">
      {mobileItems.map((item) => (
        <NavLink
          key={item.id}
          to={item.path}
          end={item.path === "/"}
          aria-label={item.label}
          className={({ isActive }) => cn("mobile-nav-link", isActive && "active")}
        >
          <Icon name={item.icon as IconName} size={22} />
          <span>{shortLabels[item.id] ?? item.label}</span>
        </NavLink>
      ))}
      <button type="button" className="mobile-nav-link" onClick={onAccount} aria-label="Account">
        <Icon name="user" size={22} />
        <span>Me</span>
      </button>
    </nav>
  )
}
